import { useEffect, useState, useRef, ReactNode, useCallback } from 'react';
import { Platform } from 'react-native';
import BackgroundService from 'react-native-background-actions';
import { sleep } from '@matap/shared';
import { useUser } from '@/hooks/use.user';
import { useUserInteraction } from '@/hooks/use.user.interaction';

// --- TYPES ---
interface WakeLockSentinel {
  released: boolean;
  release: () => Promise<void>;
}

// --- ANDROID NATIVE CONFIG ---
const backgroundSocketTask = async (taskDataArguments: any) => {
  const interval = taskDataArguments?.sleepDuration || 5000;

  while (BackgroundService.isRunning()) {
    await sleep(interval);
  }
};

const getAndroidOptions = (title: string, desc: string) => ({
  taskName: 'MatapKeepAlive',
  taskTitle: title,
  taskDesc: desc,
  taskIcon: {
    name: 'ic_launcher', // WARNING: Ensure this exact icon name exists in your res/mipmap folders!
    type: 'mipmap',
  },
  linkingURI: 'matap://main',
  foregroundServiceType: ['specialUse'] as any,
  color: '#ffffff',
  parameters: {
    sleepDuration: 5000,
  },
});

// --- PROVIDER COMPONENT ---
export const KeepAliveProvider = ({ children }: { children: ReactNode }) => {
  const [isWebVisible, setIsWebVisible] = useState(Platform.OS === 'web' && typeof document !== 'undefined' ? document.visibilityState === 'visible' : true);
  const [pendingWebLock, setPendingWebLock] = useState(false);

  const { userState } = useUser();
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  const isActiveRef = useRef(false);
  const currentOptionsRef = useRef({ title: '', desc: '' });
  const operationQueue = useRef<Promise<void>>(Promise.resolve());

  // ==========================================
  // WEB SPECIFIC HANDLERS
  // ==========================================
  const acquireWebLock = useCallback(async () => {
    if (Platform.OS !== 'web' || !('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      setPendingWebLock(false);
    } catch (err: any) {
      console.error(`Wake Lock error: ${err.name}, ${err.message}`);
      setPendingWebLock(false);
    }
  }, []);

  useUserInteraction(acquireWebLock, pendingWebLock);

  const releaseWebLock = useCallback(async () => {
    if (wakeLockRef.current && !wakeLockRef.current.released) {
      await wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleVisibilityChange = async () => {
      const visible = document.visibilityState === 'visible';
      setIsWebVisible(visible);

      if (!isActiveRef.current) return;

      if (visible && (!wakeLockRef.current || wakeLockRef.current?.released)) {
        setPendingWebLock(true);
      } else if (!visible) {
        await releaseWebLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [releaseWebLock]);

  // ==========================================
  // UNIFIED PUBLIC API (Queued)
  // ==========================================
  const startKeepAlive = useCallback(
    (title: string, description: string) => {
      operationQueue.current = operationQueue.current
        .then(async () => {
          const isAlreadyActive = isActiveRef.current;
          const optionsChanged = currentOptionsRef.current.title !== title || currentOptionsRef.current.desc !== description;

          if (isAlreadyActive && Platform.OS === 'android' && optionsChanged) {
            try {
              currentOptionsRef.current = { title, desc: description };
              await BackgroundService.updateNotification(getAndroidOptions(title, description));
            } catch (error) {
              console.error('Failed to update notification', error);
            }
            return;
          }

          if (isAlreadyActive) return;

          // Optimistically lock the ref BEFORE the async network/bridge gap
          // to prevent rapid double-firings.
          isActiveRef.current = true;
          currentOptionsRef.current = { title, desc: description };

          if (Platform.OS === 'web') {
            if (isWebVisible) setPendingWebLock(true);
          } else if (Platform.OS === 'android') {
            try {
              await BackgroundService.start(backgroundSocketTask, getAndroidOptions(title, description));
            } catch (error) {
              isActiveRef.current = false; // Rollback on failure
              console.error('Failed to start Android foreground service', error);
            }
          }
        })
        .catch(console.error);
    },
    [isWebVisible],
  );

  const stopKeepAlive = useCallback(() => {
    operationQueue.current = operationQueue.current
      .then(async () => {
        if (!isActiveRef.current) return;

        // Optimistically unlock the ref
        isActiveRef.current = false;

        if (Platform.OS === 'web') {
          setPendingWebLock(false);
          await releaseWebLock();
        } else {
          await BackgroundService.stop();
        }

        currentOptionsRef.current = { title: '', desc: '' };
      })
      .catch(console.error);
  }, [releaseWebLock]);

  // State-driven orchestration
  useEffect(() => {
    if (userState?.visit) {
      startKeepAlive('در حال مشاوره...', 'شما یک مشاوره فعال دارید');
    } else if (userState?.queueList?.length) {
      startKeepAlive('بیمار(ها) در صف انتظار', 'بیمار(ها) در صف انتظار مشاوره هستند');
    } else if (userState?.ready) {
      startKeepAlive('آماده پذیرش', 'وضعیت کاری شما فعال است');
    } else {
      stopKeepAlive();
    }
  }, [startKeepAlive, stopKeepAlive, userState]);

  // Safe cleanup on unmount using the queued stop function
  useEffect(() => {
    return () => {
      stopKeepAlive();
    };
  }, [stopKeepAlive]);

  return <>{children}</>;
};
